import { reportError } from "utils/diagnostics";
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { FieldValues, useForm, useFormState } from "react-hook-form";
import { FieldError, PhoneFormInputWrapper, StyledTextField } from "components/common/Input";
import { Popup } from "components/common/popup/popup.component";
import { Spinner } from "components/common/spinner/spinner.component";
import { emailRegex, SUPPORT_USERNAME_PREFIX } from "utils/constants";
import { stripInvisibleChars } from "utils/util-functions";
import { getSupportUserInfo } from "api/PowerUserHelpers";
import { showSuccess } from "utils/notifications";
import CustomPhoneNumber from "components/common/phone-input/phone-input.component";

interface ProvisionSupportUserDialogProps {
  open: boolean;
  userId: string;
  onClose: () => void;
  onProvisioned: () => void;
}

const ProvisionSupportUserDialog = ({
  open,
  userId,
  onClose,
  onProvisioned,
}: ProvisionSupportUserDialogProps): JSX.Element => {
  const [phoneNum, setPhoneNum] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  }: FieldValues = useForm();
  const formState = useFormState({ control });

  useEffect(() => {
    setValue("email", "");
    setValue("password", "");
    setPhoneNum("");
  }, []);

  const onModalClose = (event: never, reason: string) => {
    if (reason !== "backdropClick") {
      onClose();
    }
  };

  const onProvision = async ({ email, password }: Record<string, string>) => {
    if (!phoneNum || !isPossiblePhoneNumber(phoneNum)) return;

    setIsSaving(true);
    try {
      await getSupportUserInfo({
        username: userId,
        supportname: SUPPORT_USERNAME_PREFIX,
        action: "create",
        email: stripInvisibleChars(email),
        phonenumber: phoneNum,
        password,
      });
      showSuccess("Support user provisioned");
      onProvisioned();
      onClose();
    } catch (error) {
      reportError("support_users.onProvision", error);
      // errors are surfaced by the axios interceptor
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Popup
      title="Provision Support User"
      open={open}
      onClose={onModalClose}
      disableEscapeKeyDown
      primaryBtn={{ text: "Provision", onClick: handleSubmit(onProvision), disabled: isSaving }}
      secondaryBtn={{ text: "Cancel", onClick: onClose }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box sx={{ marginBottom: "8px", fontSize: "13px", opacity: 0.8 }}>
          A new &quot;{SUPPORT_USERNAME_PREFIX} N&quot; account is created (disabled, no organization) with the password
          you set here. Share that password with the support team.
        </Box>

        <StyledTextField
          label="Email"
          {...register("email", {
            required: "Email is required!",
            pattern: {
              value: emailRegex,
              message: "Entered value does not match email format!",
            },
          })}
          error={errors.email}
        />
        {errors.email && <FieldError>{errors.email.message}</FieldError>}

        <StyledTextField
          label="Password"
          type="password"
          {...register("password", {
            required: "Password is required!",
            minLength: { value: 8, message: "Password must be at least 8 characters" },
          })}
          error={errors.password}
        />
        {errors.password && <FieldError>{errors.password.message}</FieldError>}

        <PhoneFormInputWrapper
          international
          inputComponent={CustomPhoneNumber}
          defaultCountry="US"
          countryCallingCodeEditable={false}
          value={phoneNum}
          onChange={(e: string) => {
            setPhoneNum(e);
          }}
        />
        {formState?.submitCount > 0 && (
          <FieldError>
            {phoneNum
              ? isPossiblePhoneNumber(phoneNum)
                ? undefined
                : "Phone number is not valid!"
              : "Phone number is required!"}
          </FieldError>
        )}
      </Box>

      <Popup title="Provisioning..." open={isSaving}>
        <Spinner />
      </Popup>
    </Popup>
  );
};

export default ProvisionSupportUserDialog;
